import { ReactNode, useEffect, useRef, useState } from 'react'
import { Input, Loading } from 'react-daisyui'

interface InlineEditInputProps {
  value: string
  onSave: (newValue: string) => Promise<void>
  children: ReactNode
  size?: 'lg' | 'sm'
  className?: string
}

export default function InlineEditInput({ value, onSave, children, size = 'lg', className }: InlineEditInputProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  async function save() {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === value) {
      setEditValue(value)
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onSave(trimmed)
      setIsEditing(false)
    } catch {
      setEditValue(value)
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      save()
    }
    if (e.key === 'Escape') {
      setEditValue(value)
      setIsEditing(false)
    }
  }

  return (
    <div className={`relative inline-block cursor-pointer ${className || ''}`} onClick={() => !isEditing && setIsEditing(true)}>
      {/* Hidden children to preserve size */}
      <div className={isEditing ? 'invisible' : ''}>
        {children}
      </div>

      {/* Input overlay */}
      {isEditing && (
        <div className="absolute inset-0 flex items-center">
          <Input
            ref={inputRef}
            color='primary'
            size={size}
            className="w-full px-1 h-full font-inherit text-inherit"
            style={{ font: 'inherit' }}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={save}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
          />
          {isSaving && (
            <div className="absolute right-0 top-1/2 -translate-y-1/2">
              <Loading size="xs" color="primary" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
